import { db } from "../firebaseConfig";
import { convertFirestoreDatesToJsDates } from "../utils/firestoreConverters";
import { convertListingToProcessedListingData } from "../utils/listings/parser";
import {
  PROCESSING_LISTINGS_COLLECTION_NAME,
  REAL_ESTATE_LISTINGS_COLLECTION_NAME,
  ProcessedListingData
} from "../@types/sharedSchema";
import type { Listing } from "../@types/listings/listing";

/**
 * Lazy fetcher for listings referenced from signature documents. Keyed by
 * listing id and deduped across the page so multiple cards referencing the
 * same listing only fetch once.
 *
 * Reads the processed mirror (`yad2-listings-latest`) first, then falls back
 * to the source `real-estate-listings` document. The fallback matters: the
 * mirror only holds webot listings whose status is `onSale` — the sync Cloud
 * Function deletes the mirror doc for any other status — and contracts are
 * routinely sent exactly when a listing moves to inNegotiation/sold. The two
 * collections share the same doc id for webot listings.
 */

const listingCache = new Map<string, Promise<ProcessedListingData | null>>();

const fetchFromMirror = async (listingId: string): Promise<ProcessedListingData | null> => {
  const snap = await db.collection(PROCESSING_LISTINGS_COLLECTION_NAME).doc(listingId).get();
  if (!snap.exists) return null;
  const data = convertFirestoreDatesToJsDates(snap.data()) as ProcessedListingData;
  return { ...data, id: listingId } as ProcessedListingData;
};

const fetchFromSourceListing = async (
  listingId: string
): Promise<ProcessedListingData | null> => {
  const snap = await db.collection(REAL_ESTATE_LISTINGS_COLLECTION_NAME).doc(listingId).get();
  if (!snap.exists) return null;
  // Same converter the mirror sync Cloud Function runs, so the fallback shape
  // matches what the mirror would have held (dates stay Timestamps until the
  // converter's own toDate() calls).
  const listing = { ...(snap.data() as Listing), id: listingId };
  return { ...convertListingToProcessedListingData(listing), id: listingId };
};

export const fetchListingById = (listingId: string): Promise<ProcessedListingData | null> => {
  if (!listingId) return Promise.resolve(null);
  const cached = listingCache.get(listingId);
  if (cached) return cached;

  const promise = fetchFromMirror(listingId)
    .catch(() => null)
    .then((fromMirror) => fromMirror ?? fetchFromSourceListing(listingId))
    .catch((err: unknown) => {
      // eslint-disable-next-line no-console
      console.warn(`Failed to load listing ${listingId}:`, err);
      return null;
    });

  listingCache.set(listingId, promise);
  return promise;
};
